import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Um lugar que espera uma foto e só uma ("Frasco", "Caixa", "Etiqueta"): o rótulo em caixa alta,
 * o quadrado com a foto ou vazio, e embaixo os caminhos para preencher, de igual peso. Vazio e
 * obrigatório é pendência: `pending` marca o estado em plum ("pendente"). A troca de uma foto que
 * já existe usa os mesmos caminhos; o componente não decide o que acontece com a anterior.
 */
export interface PhotoSlotProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  label: string
  /** A foto que ocupa o lugar; sem ela, o quadrado fica vazio. */
  photo?: { src: string | null; alt: string; href?: string } | null
  /** Sem foto e obrigatório: o estado em plum. */
  pending?: boolean
  /** O texto do estado quando `pending` ("pendente"). */
  pendingLabel?: string
  /** O que está acontecendo no lugar da foto ("Enviando…"). */
  busyLabel?: string | null
  /** Os caminhos para preencher ou trocar: botões em texto, de igual peso. */
  actions?: ReactNode
  /** A causa quando o envio falhou; fica até a pessoa tentar de novo. */
  error?: string | null
}

export function PhotoSlot({
  label,
  photo,
  pending,
  pendingLabel = 'pendente',
  busyLabel,
  actions,
  error,
  className,
  ...props
}: PhotoSlotProps) {
  const isBusy = Boolean(busyLabel)
  const image = photo?.src ? (
    <img src={photo.src} alt={photo.alt} loading="lazy" className="block size-full object-cover" />
  ) : null
  return (
    <section
      data-slot="photo-slot"
      data-pending={pending || undefined}
      aria-label={label}
      className={cn('flex min-w-0 flex-col gap-2', className)}
      {...props}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="eyebrow">{label}</span>
        {pending && !image && !isBusy ? (
          <span className="font-body text-eyebrow text-plum font-light tracking-normal">
            {pendingLabel}
          </span>
        ) : null}
      </div>
      <div
        className={cn(
          'bg-pampas aspect-square w-full overflow-hidden border',
          error ? 'border-plum' : pending && !image ? 'border-hairline-plum' : 'border-hairline',
          isBusy && 'bg-alto/60 animate-pulse',
        )}
      >
        {isBusy ? (
          <span
            role="status"
            className="font-body text-eyebrow text-label flex size-full items-center justify-center font-light tracking-normal"
          >
            {busyLabel}
          </span>
        ) : image && photo?.href ? (
          <a href={photo.href} target="_blank" rel="noopener" className="block size-full">
            {image}
          </a>
        ) : (
          image
        )}
      </div>
      {error ? (
        <p
          role="alert"
          className="font-body text-eyebrow text-plum leading-[1.5] font-light tracking-normal"
        >
          {error}
        </p>
      ) : null}
      {actions ? (
        <div className="font-body text-eyebrow text-label flex flex-wrap gap-x-3 gap-y-1 font-light tracking-normal">
          {actions}
        </div>
      ) : null}
    </section>
  )
}
